import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type TaskStatus = 'PENDING' | 'IN_PROGRESS' | 'IN_REVIEW' | 'COMPLETED';
export type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export interface Task {
  id: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  progress: number;
  prompt: string | null;
  priority: Priority;
  dueDate: string | null;
  startDate: string | null;
  completedAt: string | null;
  projectId: string;
  assigneeUserId: string | null;
  assigneeUser: {
    id: string;
    name: string | null;
    email: string;
    image: string | null;
  } | null;
  assigneeAgent: string | null;
  order: number;
  createdAt: string;
  updatedAt: string;
  project?: Project;
  isRecurring: boolean;
  cronExpression: string | null;
  cronTimezone: string | null;
  cronDescription: string | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  executionStatus: string | null;
  executionResult: string | null;
  executionLogs: string | null;
  executedAt: string | null;
  executionDuration: number | null;
}

export interface Project {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    tasks: number;
  };
}

interface TaskStore {
  tasks: Task[];
  projects: Project[];
  selectedProjectId: string | null;
  isLoading: boolean;
  setTasks: (tasks: Task[]) => void;
  setProjects: (projects: Project[]) => void;
  addTask: (task: Task) => void;
  updateTask: (id: string, data: Partial<Task>) => void;
  removeTask: (id: string) => void;
  setSelectedProjectId: (id: string | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useTaskStore = create<TaskStore>()(
  persist(
    (set) => ({
      tasks: [],
      projects: [],
      selectedProjectId: null,
      isLoading: false,
      setTasks: (tasks) => set({ tasks }),
      setProjects: (projects) => set({ projects }),
      addTask: (task) => set((state) => ({ tasks: [...state.tasks, task] })),
      updateTask: (id, data) =>
        set((state) => ({
          tasks: state.tasks.map((t) => (t.id === id ? { ...t, ...data } : t)),
        })),
      removeTask: (id) =>
        set((state) => ({
          tasks: state.tasks.filter((t) => t.id !== id),
        })),
      setSelectedProjectId: (id) => set({ selectedProjectId: id }),
      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'task-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ selectedProjectId: state.selectedProjectId }),
    }
  )
);